import React, { ReactElement, useEffect, useState } from 'react'
import get from 'lodash/get'

import Loader from '@mindfulchefuk/components/Loader'
import { RecipeDetailsInfo } from '@mindfulchefuk/components/Recipes/RecipeDetails/RecipeDetailsInfo'
import RecipeDetailsBackButton from '@mindfulchefuk/components/Recipes/RecipeDetails/RecipeDetailsBackButton'

import RecipeSchema from '@mindfulchefuk/components/Recipes/RecipeSchema'

import availablePortionsByMacros from '@mindfulchefuk/components/Recipes/PortionSize/helpers/availablePortionsByMacros'
import currentMacros from '@mindfulchefuk/components/Recipes/PortionSize/helpers/currentMacros'

import {
  PORTION_SINGLE,
  PORTION_DOUBLE,
  PORTION_FAMILY,
  PORTION_SIZES,
  SEARCH_FILTER_PRIMARY_FOOD,
  SEARCH_FILTER_CUISINE,
  SEARCH_FILTER_COOKINGTIME,
} from '@mindfulchefuk/constants'
import useRecipeData from '@mindfulchefuk/hooks/useRecipeData'
import MainLayout from '@mindfulchefuk/layouts/MainLayout'

import { isAuthenticated } from '@mindfulchefuk/helpers/js/authentication'
import {
  Box,
  Grid,
  IllustrationContainer,
  Image,
  Text,
} from '@mindfulchefuk/design-system'
import { PortionSize } from '@mindfulchefuk/components/Recipes/PortionSize/PortionSize'
import { RecipeIngredients } from '@mindfulchefuk/features/Recipes/components/RecipeIngredients/RecipeIngredients'
import routesConfig from '@mindfulchefuk/config/routesConfig'
import searchDurationMap from '@mindfulchefuk/helpers/js/searchDurationMap'
import { EmailCapture } from '@mindfulchefuk/features/Homepage/components'

import { ReasonsToJoinBanner } from '@mindfulchefuk/components/ReasonsToJoinBanner/ReasonsToJoinBanner'
import { RecipePageInstructions } from './RecipePageInstructions'
import { RecipePageSection } from './RecipePageSection'

type TRecipePage = {
  slug: string
  deliveryDate: string
  portionCount: string
}

export const RecipePage = ({
  slug,
  deliveryDate,
  portionCount: initialPortionCount,
}: TRecipePage): ReactElement => {
  const [portionSize, setPortionSize] = useState<number>(null)
  const isLoggedIn = isAuthenticated()

  useEffect(() => {
    setPortionSize(parseInt(initialPortionCount, 10) || 2)
    // FIX - eslint react-hooks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePortionSizeChange = (size: number) => setPortionSize(size)

  const { recipe } = useRecipeData(slug, portionSize)

  if (!recipe) return <Loader />

  const {
    allergens = [],
    possible_allergens: possibleAllergens = [],
    images,
    title,
    description: recipeDescription,
    cuisine,
    key_ingredient_description: keyIngredientDescription,
    macros_per_person_for_1_portion: macrosOnePortion,
    macros_per_person_for_2_portion: macrosTwoPortion,
    macros_per_person_for_4_portion: macrosFourPortion,
    recipe_portion_components: recipePortionComponents,
    components,
    steps,
  } = recipe

  const foodGroup = recipe.foodGroup()

  const macrosLookup = {
    [PORTION_SINGLE]: macrosOnePortion,
    [PORTION_DOUBLE]: macrosTwoPortion,
    [PORTION_FAMILY]: macrosFourPortion,
  }

  const quantifiedIngredients = recipePortionComponents.map((ingredient) => {
    const componentId = ingredient.relationships.component.id
    const {
      ingredient: {
        description: ingredientDescrtiption,
        allergen_presences: allergenPresences,
      },
    } = components.find((component) => component.id === componentId)

    const { ingredientAllergens, ingredientPossibleAllergens } =
      allergenPresences.reduce(
        (acc, allergenPresence) => {
          if (allergenPresence.contains === 'yes') {
            acc.ingredientAllergens.push(allergenPresence.allergen)
          } else if (allergenPresence.contains === 'maybe') {
            acc.ingredientPossibleAllergens.push(allergenPresence.allergen)
          }
          return acc
        },
        { ingredientAllergens: [], ingredientPossibleAllergens: [] }
      )

    return {
      id: ingredient.id,
      displayTitle: ingredient.display_title,
      description: ingredientDescrtiption,
      allergens: ingredientAllergens,
      possibleAllergens: ingredientPossibleAllergens,
    }
  })

  const availablePortions = availablePortionsByMacros({
    macrosLookup,
    portionSizes: PORTION_SIZES,
  })

  if (
    availablePortions.length > 0 &&
    !availablePortions.includes(portionSize)
  ) {
    setPortionSize(availablePortions[0])
  }

  const macros = currentMacros({ macrosLookup, portionSize })
  const cookingTime = recipe.cookingTime()
  const cookingTimeString = `${cookingTime} mins`

  const nutritionalInformation = [
    {
      name: 'Calories',
      value: `${Math.round(get(macros, 'calories', 0))}`,
    },
    {
      name: 'Protein',
      value: `${Math.round(get(macros, 'protein', 0))}g`,
    },
    {
      name: 'Carbs',
      value: `${Math.round(get(macros, 'carbohydrates', 0))}g`,
    },
    {
      name: 'Fat',
      value: `${Math.round(get(macros, 'fat', 0))}g`,
    },
  ]

  const metaInfo = [
    {
      name: 'Cook time',
      value: cookingTimeString,
      href: `${
        routesConfig.recipes.pathname
      }?multiRange[${SEARCH_FILTER_COOKINGTIME}]=${searchDurationMap(
        cookingTime
      )}`,
    },
    {
      name: 'Cuisine',
      value: cuisine,
      href: `${routesConfig.recipes.pathname}?menu[${SEARCH_FILTER_CUISINE}]=${cuisine}`,
    },
    {
      name: 'Food group',
      value: foodGroup,
      href: `${routesConfig.recipes.pathname}?menu[${SEARCH_FILTER_PRIMARY_FOOD}]=${foodGroup}`,
    },
    {
      name: 'Key ingredient',
      value: keyIngredientDescription,
    },
    { name: 'Allergens', value: allergens.join(', ') },
    { name: 'May contain', value: possibleAllergens.join(', ') },
  ]

  const image = get(images, `[0].url`, '')
  const canonical =
    typeof window === 'undefined'
      ? ''
      : window.location.pathname.replace('/', '')
  const pageTitle = `${title} Recipe | Mindful Chef`
  const pageDescription = `Try this delicious, healthy ${title.toLowerCase()} recipe for a balanced diet packed with flavour. Cook time ${cookingTime} minutes.`

  return (
    <MainLayout
      title={pageTitle}
      canonical={canonical}
      description={pageDescription}
    >
      <Box overflow="hidden" color="aubergine">
        <IllustrationContainer illustrations="07">
          <RecipeSchema
            name={title}
            description={recipeDescription}
            image={image}
            cookTime={`${recipe.cookingTime()} minutes`}
            ingredients={recipePortionComponents}
            nutritionalInformation={nutritionalInformation}
            steps={steps}
          />
          <Box py={24}>
            <RecipeDetailsBackButton deliveryDate={deliveryDate} />
          </Box>
          <Grid
            templateColumns={{ base: '1fr', md: '350px 1fr', lg: '1fr 700px' }}
            columnGap={{ base: 24, md: 40 }}
            rowGap={12}
            mb={32}
          >
            <Box position="relative" z={2} mx={{ base: -24, md: 0 }}>
              <Image src={image} width={512} alt="" />
            </Box>
            <Box pb={16} color="aubergine">
              <Text variant={['heading-md', 'heading-lg']} as="h1" mb={16}>
                {title}
              </Text>
              <Text variant="body-md" mb={24}>
                {recipeDescription}
              </Text>
              <RecipeDetailsInfo info={metaInfo} />
            </Box>
            <Box>
              <PortionSize
                availablePortions={availablePortions}
                portionSize={portionSize}
                onClick={handlePortionSizeChange}
              />

              <RecipePageSection title="Ingredients">
                <RecipeIngredients
                  quantifiedIngredients={quantifiedIngredients}
                  textVariant="body-md"
                />
              </RecipePageSection>
              <RecipePageSection title="Nutritional Information">
                <RecipeDetailsInfo info={nutritionalInformation} />
              </RecipePageSection>
            </Box>

            <Box>
              {!isLoggedIn && <ReasonsToJoinBanner />}

              <Box gridRow={[5, 5, 'auto']}>
                <RecipePageSection title="Cooking Instructions">
                  <RecipePageInstructions recipe={recipe} />
                </RecipePageSection>
              </Box>
            </Box>
          </Grid>
        </IllustrationContainer>
      </Box>
      <EmailCapture source="mc_recipelibrary" />
    </MainLayout>
  )
}
